import { Component, Input } from '@angular/core';
import { BeLembrete } from 'src/app/models/BeLembrete';

export interface LembretesCalendarioDialogData {
  lembretes: BeLembrete[];
  podeAlterar: (lembrete: BeLembrete) => boolean;
  podeExcluir: (lembrete: BeLembrete) => boolean;
  onSalvar: (lembrete: BeLembrete, aoSalvar: (lembreteSalvo: BeLembrete) => void) => void;
  onAcao: (evento: { chave: string; linha: BeLembrete }, aoConcluir?: () => void) => void;
  onExcluir: (lembrete: BeLembrete, aoExcluir: () => void) => void;
}

interface DiaCalendario {
  data: Date;
  pertenceAoMes: boolean;
}

@Component({
  selector: 'app-lembretes-calendario',
  templateUrl: './lembretes-calendario-dialog.component.html',
  styleUrls: ['./lembretes-calendario-dialog.component.css']
})
export class LembretesCalendarioDialogComponent {
  @Input() public data!: LembretesCalendarioDialogData;

  public readonly diasDaSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  public mesExibido: Date;
  public lembreteSelecionado?: BeLembrete;
  public lembreteEmEdicao?: BeLembrete;
  public chaveDiaEmEdicao?: string;

  public constructor() {
    const hoje = new Date();
    this.mesExibido = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  }

  public get tituloMes(): string {
    const titulo = this.mesExibido.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return titulo.charAt(0).toUpperCase() + titulo.slice(1);
  }

  public get dias(): DiaCalendario[] {
    const inicioMes = new Date(this.mesExibido.getFullYear(), this.mesExibido.getMonth(), 1);
    const inicioCalendario = new Date(inicioMes.getFullYear(), inicioMes.getMonth(), 1 - inicioMes.getDay());

    return Array.from({ length: 42 }, (_, indice) => {
      const data = new Date(inicioCalendario.getFullYear(), inicioCalendario.getMonth(), inicioCalendario.getDate() + indice);
      return {
        data,
        pertenceAoMes: data.getMonth() === this.mesExibido.getMonth()
      };
    });
  }

  public mudarMes(deslocamento: number): void {
    this.cancelarEdicao();
    this.mesExibido = new Date(this.mesExibido.getFullYear(), this.mesExibido.getMonth() + deslocamento, 1);
  }

  public lembretesDoDia(dia: DiaCalendario): BeLembrete[] {
    return this.data.lembretes.filter(lembrete => this.ehMesmoDia(this.obterDataLembrete(lembrete), dia.data));
  }

  public obterHorario(lembrete: BeLembrete): string {
    return lembrete.dtAgendado?.split('T')[1]?.substring(0, 5) ?? '';
  }

  public obterDataPreview(data?: Date | null): string {
    if (!data) {
      return '';
    }

    const dataFormatada = new Date(data as unknown as string | number | Date);
    return Number.isNaN(dataFormatada.getTime()) ? '' : dataFormatada.toLocaleDateString('pt-BR');
  }

  public excluirLembrete(): void {
    if (this.lembreteSelecionado) {
      this.data.onExcluir(this.lembreteSelecionado, () => this.removerLembreteDoCalendario());
    }
  }

  public iniciarNovoLembrete(dia: DiaCalendario): void {
    const agora = new Date();
    const data = new Date(dia.data.getFullYear(), dia.data.getMonth(), dia.data.getDate());
    if (this.ehMesmoDia(data, agora)) {
      data.setHours(agora.getHours(), agora.getMinutes() + 5, 0, 0);
    } else {
      data.setHours(9, 0, 0, 0);
    }

    this.lembreteSelecionado = undefined;
    this.lembreteEmEdicao = {
      id: null,
      dtAgendado: '',
      dsLembrete: '',
      noStatus: 'Agendado',
      blAtivo: true,
      dtAgendadoData: data,
      hrAgendado: this.formatarHorario(data)
    };
    this.chaveDiaEmEdicao = this.chaveData(dia.data);
  }

  public editarLembrete(lembrete: BeLembrete, evento: MouseEvent): void {
    evento.stopPropagation();
    this.lembreteSelecionado = lembrete;

    const data = this.obterDataLembrete(lembrete);
    this.lembreteEmEdicao = {
      ...lembrete,
      dtAgendadoData: data,
      hrAgendado: this.obterHorario(lembrete)
    };
    this.chaveDiaEmEdicao = data ? this.chaveData(data) : undefined;
  }

  public cancelarEdicao(): void {
    this.lembreteEmEdicao = undefined;
    this.lembreteSelecionado = undefined;
    this.chaveDiaEmEdicao = undefined;
  }

  public podeSalvarEdicao(): boolean {
    return (!this.lembreteEmEdicao?.id || this.data.podeAlterar(this.lembreteEmEdicao)) &&
      !!this.lembreteEmEdicao?.dtAgendadoData &&
      !!this.lembreteEmEdicao.dsLembrete?.trim() &&
      /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(this.lembreteEmEdicao.hrAgendado ?? '');
  }

  public salvarEdicao(): void {
    if (!this.lembreteEmEdicao || !this.podeSalvarEdicao()) {
      return;
    }

    const dtAgendado = this.montarDataHora(this.lembreteEmEdicao.dtAgendadoData, this.lembreteEmEdicao.hrAgendado);
    if (!dtAgendado) {
      return;
    }

    const lembrete = {
      ...this.lembreteEmEdicao,
      dtAgendado,
      dsLembrete: this.lembreteEmEdicao.dsLembrete.trim()
    };
    this.data.onSalvar(lembrete, lembreteSalvo => {
      const indice = this.data.lembretes.findIndex(item => item.id === lembreteSalvo.id);
      if (indice >= 0) {
        Object.assign(this.data.lembretes[indice], lembreteSalvo);
      } else {
        this.data.lembretes = [...this.data.lembretes, lembreteSalvo];
      }
      this.cancelarEdicao();
    });
  }

  public estaEditandoDia(dia: DiaCalendario): boolean {
    return this.chaveDiaEmEdicao === this.chaveData(dia.data);
  }

  public abrirBalaoParaCima(dia: DiaCalendario): boolean {
    return this.dias.findIndex(item => this.chaveData(item.data) === this.chaveData(dia.data)) >= 21;
  }

  public get textoAcaoAtivo(): string | null {
    if (!this.lembreteSelecionado || !this.data.podeAlterar(this.lembreteSelecionado)) {
      return null;
    }

    if (this.lembreteSelecionado.blAtivo && this.lembreteSelecionado.noStatus === 'Agendado') {
      return 'Desativar';
    }

    if (!this.lembreteSelecionado.blAtivo && this.lembreteSelecionado.noStatus === 'Cancelado') {
      return 'Ativar';
    }

    return null;
  }

  public alterarAtivo(): void {
    if (!this.lembreteSelecionado || !this.textoAcaoAtivo) {
      return;
    }

    this.data.onAcao({
      chave: this.lembreteSelecionado.blAtivo ? 'desativar' : 'ativar',
      linha: this.lembreteSelecionado
    }, () => this.cancelarEdicao());
  }

  public ehHoje(dia: DiaCalendario): boolean {
    return this.ehMesmoDia(dia.data, new Date());
  }

  public readonly rastrearDia = (_: number, dia: DiaCalendario): string => this.chaveData(dia.data);

  public rastrearLembrete(_: number, lembrete: BeLembrete): number | string {
    return lembrete.id ?? `${lembrete.dtAgendado}-${lembrete.dsLembrete}`;
  }

  private obterDataLembrete(lembrete: BeLembrete): Date | null {
    const data = lembrete.dtAgendado?.split('T')[0];
    if (!data) {
      return null;
    }

    const [ano, mes, dia] = data.split('-').map(Number);
    if (!ano || !mes || !dia) {
      return null;
    }

    return new Date(ano, mes - 1, dia);
  }

  private ehMesmoDia(dataA: Date | null, dataB: Date): boolean {
    return !!dataA && dataA.getFullYear() === dataB.getFullYear() &&
      dataA.getMonth() === dataB.getMonth() && dataA.getDate() === dataB.getDate();
  }

  private chaveData(data: Date): string {
    return `${data.getFullYear()}-${data.getMonth()}-${data.getDate()}`;
  }

  private removerLembreteDoCalendario(): void {
    const idLembrete = this.lembreteSelecionado?.id;
    this.data.lembretes = this.data.lembretes.filter(lembrete => lembrete.id !== idLembrete);
    this.cancelarEdicao();
  }

  private montarDataHora(data?: Date | null, horario?: string): string | null {
    if (!data || Number.isNaN(data.getTime()) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(horario ?? '')) {
      return null;
    }

    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}T${horario}:00`;
  }

  private formatarHorario(data: Date): string {
    return `${String(data.getHours()).padStart(2, '0')}:${String(data.getMinutes()).padStart(2, '0')}`;
  }
}
